// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Stock, Bom } from "../stock/index.js"

// @@imports-module
import { AdhesiveCalculator } from "./Adhesive.js"

// @@imports-types
import {
    Config,
    EdgeType,
    ADHESIVE_TYPES,
    EDGE_TYPES,
    TAPE_TYPES,
    MEMBRANE_TYPES,
    JOIN_TYPES
} from "../types/index.js"
import { TapeCalculator } from "./Tape.js"
import { Trim } from "../products/Trim.js"

// @@body
// Declare global variables.
const MIN_OFFCUT = 0.25
const METAL_EDGE_OVERLAP = 0.1

/**
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 * @todo Configure use of offcuts object (requires straight joiners).
 * @todo Remove config property if unused.
 */
class TrimCalculator {
    #stock
    #config
    #adhesives
    #tapes
    #bom = new Bom()
    #offcuts = /** @type {Object.<EdgeType, number[]>} */ ({
        houseWall: /** @type {number[]} */ ([]),
        feltJoin: /** @type {number[]} */ ([]),
        raisedKerb: /** @type {number[]} */ ([]),
        gutterDrip: /** @type {number[]} */ ([])
    })

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config 
     * @param {AdhesiveCalculator} adhesiveCalculator
     * @param {TapeCalculator} tapeCalculator
     */
    constructor(stock, config, adhesiveCalculator, tapeCalculator) {
        this.#stock = stock
        this.#config = config
        this.#adhesives = adhesiveCalculator
        this.#tapes = tapeCalculator
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @param {number} length 
     */
    addTrim(edgeType, length) {
        const isTapeRequired = edgeType === EDGE_TYPES.pitchRoofOff ||
            edgeType === EDGE_TYPES.seamJoin ||
            edgeType === EDGE_TYPES.buttJoin
        if (isTapeRequired) {
            const { membrane: { type }, rubberJoin } = this.#config
            const requiresCover = type === MEMBRANE_TYPES.fleeceBacked ||
                rubberJoin === JOIN_TYPES.butt ||
                edgeType === EDGE_TYPES.buttJoin

            const tapeType = requiresCover ? TAPE_TYPES.cured : TAPE_TYPES.seam
            this.#tapes.addTape(length, tapeType)

            return
        }

        const isTrimRequired = edgeType === EDGE_TYPES.houseWall ||
            edgeType === EDGE_TYPES.gutterDrip ||
            edgeType === EDGE_TYPES.raisedKerb ||
            edgeType === EDGE_TYPES.feltJoin
        if (!isTrimRequired) { return }

        const trims = this.#stock
            .getTrims(edgeType)
            .sort((trimA, trimB) => trimA.quantity - trimB.quantity)
        if (!trims.length) { throw new Error() }

        let lengthRemaining = length
        TRIM_TILING_LOOP: while (lengthRemaining > 0) {
            const offcutRequired = Math.max(lengthRemaining, MIN_OFFCUT)
            for (let i = 0; i < this.#offcuts[edgeType].length; i++) {
                const offcut = this.#offcuts[edgeType][i]
                if (offcut >= offcutRequired) {
                    this
                        .#offcuts[edgeType]
                        .splice(i, 1, offcut - offcutRequired)
                    break TRIM_TILING_LOOP
                }
            }

            const trim = /** @type {Trim} */ (trims.find(trim => {
                return trim.quantity >= lengthRemaining &&
                    trim.runningStock > 0
            }) || trims.at(- 1))

            if (!trim) { throw new Error() }
            this.#bom.addLineItem(trim, 1)
            trim.runningStock -= 1

            lengthRemaining -= trim.quantity
            if (parseFloat(lengthRemaining.toFixed(2)) <= 0) {
                const offcut = Math.abs(lengthRemaining)
                if (offcut >= MIN_OFFCUT) {
                    this.#offcuts[edgeType].push(offcut)
                }
                break
            }
            if (edgeType === EDGE_TYPES.houseWall) {
                lengthRemaining += METAL_EDGE_OVERLAP
            }

            // Clamp minimum length remaining for next trim in tiling loop to
            // the minimum offcut length such that, when specifying multiple
            // trims for an edge, the last specified length is at least the
            // minimum offcut to give installers play with centering joins and
            // not having tiny cuts.
            lengthRemaining = Math.max(lengthRemaining, MIN_OFFCUT)
        }

        switch (edgeType) {
            case EDGE_TYPES.houseWall:
            // case EDGE_TYPES.parapetWall:
                this.#adhesives.addAdhesive(
                    ADHESIVE_TYPES.sealants.blackSilicone,
                    length
                )
                break
            case EDGE_TYPES.feltJoin:
                this.#adhesives.addAdhesive(
                    ADHESIVE_TYPES.sealants.lapEdge,
                    length
                )
                this.#adhesives.addAdhesive(
                    ADHESIVE_TYPES.sealants.waterBlock,
                    length
                )
                break
            default:
        }
    }

    get bom() {
        return this.#bom
    }
}

// @@exports
export { TrimCalculator }
